import { Contact, Label } from '../../../shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteLabel, GPanelContactsKeys } from '../../../gpanel-contacts-api';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { useStore } from '../../../../../state/zustand-store';
import { useNavigate } from 'react-router-dom';
import { GPanelContactsPagesConfig } from '../../../shared/pages-config';
import SharedDialog from '../../../../../../shared/shared-dialog';

import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export default function GpanelContactsDeleteLabelDialog({
  handleClose,
  label,
}: {
  handleClose: () => void;
  label: Label;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteLabel(label.id, changeLogMessage),
    onSuccess: (data) => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Trashed('Label'),
      });
      queryClient.setQueryData([GPanelContactsKeys.LabelGroups], data.group);
      queryClient.setQueryData<Label[]>(
        [GPanelContactsKeys.TrashLabels],
        (oldData) => {
          const trashedLabel = {
            ...label,
            trashed: true,
            trashedTime: Date.now(),
          };
          if (!oldData) return [trashedLabel];
          return [...oldData, trashedLabel];
        }
      );
      queryClient.setQueriesData<Contact>(
        { queryKey: [GPanelContactsKeys.GPanelContact] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
          return {
            ...oldData,
            labelIds: oldData.labelIds?.filter((id) => id !== label.id) ?? [],
          };
        }
      );
      queryClient.setQueryData<Contact[]>(
        [GPanelContactsKeys.OrganizationDirectoryContacts],
        (oldData) => {
          if (!oldData) {
            return oldData;
          }
        }
      );
      queryClient.setQueryData<Label[]>(
        [GPanelContactsKeys.GPanelLabels],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((l) => l.id !== label.id);
        }
      );

      navigate(GPanelContactsPagesConfig.OrganizationDirectory.link);
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Trashing('label'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.trash_label, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    const permission = GPanelContactsPermissions.TrashGpanelLabels.value;

    if (hasPermission(changeLogRoles, permission)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title="Move Label to Trash"
        dialogContent={
          <Stack gap={2}>
            <Typography>
              Move <strong>{label.name}</strong> to trash? It will be removed
              from all assigned contacts and will be permanently deleted after
              30 days.
            </Typography>
            {errorText && <FormHelperText error children={errorText} />}
          </Stack>
        }
        primaryButton={{
          title: 'Move to Trash',
          color: 'warning',
          action: handleSubmit,
          loading: isPending,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => mutate()}
        mutateLoading={isPending}
      />
    </>
  );
}
